import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { PaymentService } from '@/supabase/payment/paymentService'
import { Subscription, SubscriptionPlan, SubscriptionUsageStat } from '@/lib/payment/types'
import { useAuth } from '@/hooks/useAuth'
import { logError } from '@/lib/sentry'
import { tiktokIdentify, tiktokTrackStartTrial } from '@/lib/tiktok'

interface SubscriptionContextType {
  subscription: Subscription | null
  subscriptionPlan: SubscriptionPlan | null
  usageStat: SubscriptionUsageStat | null
  isLoading: boolean
  refreshSubscription: () => Promise<void>
  isPastDue: boolean // 결제 실패 상태인지 여부
  isCopyLimitReached: boolean; // 계산된 값으로 제공
  incrementCopyCount: () => void; // 클라이언트에서 사용량 증가
  setSubscriptionDataManually: (
    newSubscription: Subscription,
    newPlan: SubscriptionPlan,
    newUsageStat: SubscriptionUsageStat | null
  ) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined)

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlan | null>(null)
  const [usageStat, setUsageStat] = useState<SubscriptionUsageStat | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isPastDue, setIsPastDue] = useState(false)
  const [paymentService] = useState(() => new PaymentService())
  
  // 비동기 상태 관리를 위한 ref
  const dataFetchedRef = useRef<boolean>(false)
  const isRefreshingRef = useRef<boolean>(false)

  const fetchSubscriptionData = useCallback(async (userId: string) => {
    if (!userId) return
    
    if (isRefreshingRef.current) return
    
    console.log('📊 fetchSubscriptionData 시작:', {
      userId,
      time: new Date().toISOString()
    })
    
    isRefreshingRef.current = true
    setIsLoading(true)
    
    try {
      const currentSubscription = await paymentService.getCurrentSubscription(userId)
      const pastDueSubscription = await paymentService.getPastDueSubscription(userId)
      
      if (pastDueSubscription) {
        console.log('📊 fetchSubscriptionData - past_due 상태의 구독 발견:', pastDueSubscription.id)
        setSubscription(pastDueSubscription)
        setIsPastDue(true)
        
        const plan = await paymentService.getSubscriptionPlan(pastDueSubscription.subscription_plan_id)
        setSubscriptionPlan(plan)
        
        const currentUsageStat = await paymentService.getCurrentUsageStat(userId, pastDueSubscription.id)
        setUsageStat(currentUsageStat)
      }
      else if (currentSubscription) {
        console.log('📊 fetchSubscriptionData - active 상태의 구독 발견:', currentSubscription.id)
        setSubscription(currentSubscription)
        setIsPastDue(false)
        
        const currentPlan = await paymentService.getCurrentPlan(userId)
        setSubscriptionPlan(currentPlan)
        
        const currentUsageStat = await paymentService.getCurrentUsageStat(userId, currentSubscription.id)
        setUsageStat(currentUsageStat)
      }
      else {
        console.log('📊 fetchSubscriptionData - 구독 정보 없음, 무료 구독 생성 시작:', userId)
        try {
          await paymentService.createFreePremiumTrialSubscription(userId)
          const newSubscription = await paymentService.getCurrentSubscription(userId)
          setSubscription(newSubscription)
          setIsPastDue(false)
          
          const premiumTrialPlan = await paymentService.getSubscriptionPlan('premium')
          setSubscriptionPlan(premiumTrialPlan)
          if (premiumTrialPlan) {
            tiktokTrackStartTrial(premiumTrialPlan)
          }
          
          if (newSubscription) {
            const currentUsageStat = await paymentService.getCurrentUsageStat(userId, newSubscription.id)
            setUsageStat(currentUsageStat)
          }
        } catch (error) {
          logError('무료 프리미엄 체험판 구독 생성 중 오류 발생:', error)
        }
      }
    } catch (error) {
      logError('구독 정보 조회 중 오류 발생:', error)
    } finally {
      setIsLoading(false)
      isRefreshingRef.current = false
      console.log('📊 fetchSubscriptionData 완료:', {
        userId,
        time: new Date().toISOString(),
        isPastDue: isPastDue
      })
    }
  }, [paymentService, isPastDue])

  useEffect(() => {
    if (dataFetchedRef.current) return
    
    if (user) {
      dataFetchedRef.current = true
      fetchSubscriptionData(user.id)
      tiktokIdentify(user)
    } else {
      setSubscription(null)
      setSubscriptionPlan(null)
      setUsageStat(null)
      setIsPastDue(false)
      setIsLoading(false)
      dataFetchedRef.current = false
    }
  }, [user, fetchSubscriptionData])

  const refreshSubscription = useCallback(async () => {
    if (user) {
      await fetchSubscriptionData(user.id)
    }
  }, [user, fetchSubscriptionData])

  const setSubscriptionDataManually = useCallback((
    newSubscription: Subscription,
    newPlan: SubscriptionPlan,
    newUsageStat: SubscriptionUsageStat | null
  ) => {
    console.log('📊 setSubscriptionDataManually 호출됨:', { newSubscription, newPlan, newUsageStat });
    setSubscription(newSubscription);
    setSubscriptionPlan(newPlan);
    setUsageStat(newUsageStat);
    setIsPastDue(newSubscription.status === 'past_due');
    setIsLoading(false);
    dataFetchedRef.current = true;
    isRefreshingRef.current = false;
  }, []);

  // 사용량 한도 도달 여부를 상태로부터 직접 계산
  const isCopyLimitReached =
    !!subscriptionPlan &&
    subscriptionPlan.copy_usage_limit !== null &&
    !!usageStat &&
    usageStat.copy_used_count >= subscriptionPlan.copy_usage_limit;

  // 클라이언트에서 복사 사용량을 낙관적으로 업데이트하는 함수
  const incrementCopyCount = useCallback(() => {
    setUsageStat(prevStat => {
      if (!prevStat) return null;
      return {
        ...prevStat,
        copy_used_count: prevStat.copy_used_count + 1,
      };
    });
  }, []);

  const value = useMemo(() => ({
    subscription,
    subscriptionPlan,
    usageStat,
    isLoading,
    refreshSubscription,
    isPastDue,
    isCopyLimitReached,
    incrementCopyCount,
    setSubscriptionDataManually
  }), [
    subscription,
    subscriptionPlan,
    usageStat,
    isLoading,
    refreshSubscription,
    isPastDue,
    isCopyLimitReached,
    incrementCopyCount,
    setSubscriptionDataManually
  ])

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription() {
  const context = useContext(SubscriptionContext)
  if (context === undefined) {
    throw new Error('useSubscription은 SubscriptionProvider 내부에서만 사용할 수 있습니다')
  }
  return context
}